import PropTypes from 'prop-types'
import moment from 'moment'
import { Trans, useTranslation } from 'react-i18next'

import { GENDER_OPTIONS } from '../../misc/Constants'
import { useTranslated } from '../../i18n'
import { translateOptions } from '../../utility/i18nConverters'
import Modal from '../common/Modal'
import AvatarPlaceholder from '../../assets/images/mock/default-profile-icon.png'
import { ReactComponent as CallIcon } from '../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as MailIcon } from '../../assets/images/common/icons/mail-icon.svg'

import classes from './CoachContactInfoModal.module.scss'
import SubmitButton from '../common/buttons/SubmitButton'
import { Link } from 'react-router-dom'

const CoachContactInfoModal = ({ isOpen, onClose, coach }) => {
  const { t } = useTranslation()
  const fullName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim()
  const genderOptions = useTranslated(GENDER_OPTIONS, translateOptions)
  const genderLabel = coach.gender !== undefined && coach.gender !== null
    ? genderOptions.find(g => g.value === coach.gender)?.label || '-'
    : '-'

  const formatPhoneNumber = (phone, phonePrefix) => {
    if (!phone) { return t('coaches.contactInfo.notAvailable') }

    return phonePrefix ? `${phonePrefix}${phone}` : phone
  }

  const formatLastSignInAt = () => {
    if (!coach.last_sign_in_at) { return t('coaches.contactInfo.never') }

    return moment(coach.last_sign_in_at).format('MM/DD/YYYY')
  }

  const formatBirthday = () => {
    if (!coach.birthdate) { return '-' }

    return moment(coach.birthdate).format('MM/DD/YYYY')
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={classes.coachContactInfoModalContent}
    >
      <div className={classes.coachContactInfoModal}>
        <div className='d-flex flex-column mb-5'>
          <h2 className={classes.modalTitle}>{t('coaches.contactInfo.title')}</h2>
          <p className={classes.modalSubtitle}>
            <Trans
              i18nKey='coaches.contactInfo.securityNotice'
              components={{
                articleLink: (
                  <a
                    href='https://intercom.help/op-36/en/articles/14207593-how-to-update-your-email-or-password'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    link
                  </a>
                ),
              }}
            />
          </p>
        </div>

        <div className='pb-4'>
          <img
            src={coach.avatar?.file_url || AvatarPlaceholder}
            className={classes.coachAvatar}
            alt={t('coaches.contactInfo.avatarAlt', { name: fullName })}
          />
          <span className='font-weight-bold ml-2'>{fullName}</span>
        </div>

        <div className='d-flex flex-column'>
          <p className='font-weight-bold'>{t('coaches.contactInfo.contactInfoHeading')}</p>

          <div className='d-flex flex-column pb-2'>
            <div className={classes.contactItem}>
              <div className='d-flex justify-content-between align-items-end'>
                <span className={classes.contactText}>{formatPhoneNumber(coach.phone, coach.phone_prefix)}</span>
                <CallIcon className={classes.contactIcon} />
              </div>
            </div>

            <div className={classes.contactItem}>
              <div className='d-flex justify-content-between align-items-end'>
                <span className={classes.contactText}>{coach.email}</span>
                <MailIcon className={classes.contactIcon} />
              </div>
            </div>
          </div>
        </div>

        <div className='d-flex flex-column pb-4'>
          <p className='font-weight-bold'>{t('coaches.contactInfo.aboutHeading')}</p>

          <p className={classes.aboutItem}>
            <Trans
              i18nKey='coaches.contactInfo.communityLine'
              values={{ name: coach.communities?.[0] ? coach.communities[0].name : t('coaches.contactInfo.notAvailable') }}
              components={{
                communityLink: coach.communities?.[0]
                  ? <Link to={`/communities/${coach.communities[0].id}`} target='_blank' rel='noopener noreferrer' />
                  : <span />,
              }}
            />
          </p>
          <p className={classes.aboutItem}>
            {t('coaches.contactInfo.genderLine', { gender: genderLabel })}
          </p>
          <p className={classes.aboutItem}>
            {t('coaches.contactInfo.birthdayLine', { date: formatBirthday() })}
          </p>
          <p className={classes.aboutItem}>
            {t('coaches.contactInfo.yearsInSystemLine', { duration: moment(coach.created_at).fromNow(true) })}
          </p>
          <p className={classes.aboutItem}>
            {t('coaches.contactInfo.lastSignedInLine', { value: formatLastSignInAt() })}
          </p>
        </div>

        <div className='d-flex justify-content-center align-items-center'>
          <SubmitButton onClick={onClose} buttonMessage={t('coaches.contactInfo.gotIt')} className={classes.submitButton} />
        </div>
      </div>
    </Modal>
  )
}

CoachContactInfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  coach: PropTypes.object,
}

export default CoachContactInfoModal
